// the phone's category screen, as the top of the web hub page.
//
// Three pieces:
//   HubTabRail   the icon tabs, pinned under the site header. Real links to
//                /category/<slug>[/<city>] so every hub keeps its own URL.
//   HubTitleRow  breadcrumbs, the page's one <h1>, the city picker (links
//                again) and whatever filter controls the page passes in.
//   HubIntro     the long SEO lede, moved under the grid. Clamped to a few
//                lines with "Read more"; the full text is always in the HTML.
import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { isStayPropertyType } from "@majourneys/shared";
import { useCategories as useHomeCategories } from "../../home/hooks/use-categories";
import { useCategories as useActivityCategories } from "../hooks/use-categories";
import { useCities } from "../hooks/use-cities";
import { useCityCategoryCounts } from "../hooks/use-city-category-counts";
import { isRankedCategory, rankCategories } from "../lib/category-rank";
import { pewterFrame, pewterInner, solid } from "../../../shared/ui/pewter-edge";
import { queueCategoryFx, useQueuedCategoryFx } from "../../../shared/ui/category-tap-fx";

const NAVY = "#110E39";
const META = "#5a5d77";


/* ------------------------------------------------------------- tab rail */

function tabSlugHint(rows: unknown, slug?: string) {
  return ((rows ?? []) as { slug: string; parentSlug?: string | null }[]).find((c) => c.slug === slug)?.parentSlug ?? slug;
}

export function HubTabRail({ lang, activeSlug, citySlug }: { lang: string; activeSlug?: string; citySlug?: string }) {
  const { t } = useTranslation();
  const { data: home } = useHomeCategories();
  const { data: available } = useActivityCategories(citySlug);
  const availableSlugs = new Set(((available.data ?? []) as { slug: string }[]).map((c) => c.slug));
  // The app's order; in one city, the most stocked category first.
  const cityCounts = useCityCategoryCounts(
    lang,
    citySlug,
    available.data as { slug: string; parentSlug?: string | null }[] | undefined,
  );
  const cats = rankCategories(
    ((home.data ?? []) as { id: string; slug: string; name: string; icon?: string }[])
      .filter((c) => isRankedCategory(c.slug))
      .filter((c) => (citySlug ? availableSlugs.has(c.slug) || c.slug === activeSlug || c.slug === tabSlugHint(available.data, activeSlug) : true)),
    cityCounts,
  );

  const tabs: { slug?: string; name: string; icon?: string }[] = [
    { slug: undefined, name: t("common.all", { defaultValue: "All" }) },
    ...cats,
  ];
  // On a type page (/category/quad) the tab to light up is its parent.
  const activeRow = ((available.data ?? []) as { slug: string; parentSlug?: string | null }[]).find(
    (c) => c.slug === activeSlug,
  );
  // Hotel, riad, resort and villa are not rows in `categories`; they belong
  // under Stays.
  const tabSlug =
    activeSlug && isStayPropertyType(activeSlug) ? "stays" : activeRow?.parentSlug ?? activeSlug;
  const found = tabs.findIndex((tab) => tab.slug === tabSlug);
  const routeIdx = Math.max(0, found);
  // The line leaves for the tapped tab at once, not when the next page lands.
  const [pending, setPending] = useState<number | null>(null);
  useEffect(() => setPending(null), [routeIdx]);
  const idx = pending ?? routeIdx;

  const refs = useRef<(HTMLAnchorElement | null)[]>([]);
  const rowRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const [bar, setBar] = useState<{ x: number; w: number } | null>(null);
  // Measured against the row itself, and again whenever the row resizes
  // (web fonts and icons land after the first paint and widen the tabs).
  useEffect(() => {
    const row = rowRef.current;
    const strip = stripRef.current;
    // Measured against the inner strip, which scrolls with the tabs, so the
    // maths is the same left-to-right and right-to-left.
    const measure = () => {
      const el = refs.current[idx];
      if (!strip || !el || (found < 0 && activeSlug)) return setBar(null);
      // Layout units, not getBoundingClientRect: from 768px up the body wears
      // `zoom: 0.9` (tailwind.css), rects come back zoomed, and the line's
      // translateX is zoomed again, so a rect-measured line landed ~10% short
      // of the active tab on every desk (owner saw it under the wrong tab).
      // The strip is the tabs' offsetParent, so offsetLeft is the same frame
      // the line is positioned in, left-to-right and right-to-left alike.
      setBar({ x: el.offsetLeft + 14, w: el.offsetWidth - 28 });
    };
    // Bring the active tab into view once its width is known (icons and
    // fonts land late, so this runs again from the observer below).
    let centred = false;
    const centre = () => {
      const el = refs.current[idx];
      if (centred || !row || !el || el.offsetWidth === 0) return;
      if (row.scrollWidth > row.clientWidth) {
        const r = row.getBoundingClientRect();
        const e = el.getBoundingClientRect();
        // Rects are zoomed (see measure above); scrollLeft is not.
        const zoom = row.offsetWidth ? r.width / row.offsetWidth : 1;
        row.scrollLeft += (e.left + e.width / 2 - (r.left + r.width / 2)) / zoom;
      }
      centred = true;
    };
    const measureAndCentre = () => {
      measure();
      centre();
    };
    measure();
    const ro = new ResizeObserver(measureAndCentre);
    if (row) ro.observe(row);
    refs.current.forEach((el) => el && ro.observe(el));
    // Keep the active tab in view on a narrow screen.
    centre();
    return () => ro.disconnect();
  }, [idx, tabs.length, found, activeSlug]);

  const compact = useCompactOnScroll();

  /* The tapped category's effect plays on its tab once its page has landed
     (see shared/ui/category-tap-fx.ts). Only on the big icon: folded, the
     icon has gone and the effect would have nothing to sit on. */
  const landedStage = useRef<HTMLSpanElement | null>(null);
  const landedSlug = found >= 0 ? tabs[routeIdx]?.slug : undefined;
  useQueuedCategoryFx(landedSlug, landedStage, !compact);

  /* Publish the rail's height, so a bar that pins under it (the stays
     filter bar) sits flush without guessing. */
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--mj-hub-rail-h", `${nav.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(nav);
    return () => ro.disconnect();
  }, []);

  return (
    <nav
      ref={navRef}
      data-hub-rail=""
      aria-label={t("home.categories_nav")}
      className="sticky z-30 border-b"
      style={{
        top: "var(--mj-header-h, 69px)",
        // Scrolling down: the site header slides up and this rail rides up
        // with it, past its own icons, leaving the labels as one slim row.
        // Transform only; nothing here changes size.
        transform: compact ? `translateY(calc(-1 * (var(--mj-header-h, 69px) + ${FOLD}px)))` : "translateY(0)",
        transition: `transform 320ms ${EASE}`,
        background: "rgba(248,249,252,0.9)",
        backdropFilter: "saturate(1.4) blur(14px)",
        WebkitBackdropFilter: "saturate(1.4) blur(14px)",
        borderColor: "rgba(17,14,57,0.08)",
      }}
    >
      {/* The strip is centred with auto margins inside the scroller, not with
          justify-center: a centred flex row that overflows loses its first
          tabs off the start edge, where they cannot be scrolled back to. */}
      <div ref={rowRef} className="mx-auto w-full max-w-[1280px] overflow-x-auto px-2 pt-3 [scrollbar-width:none] md:px-8 [&::-webkit-scrollbar]:hidden">
      <div ref={stripRef} className="relative mx-auto flex w-max gap-1">
        {tabs.map((tab, i) => {
          const on = i === idx && (found >= 0 || !activeSlug);
          const className =
            // px-5 rather than px-3.5: the folded row puts a small icon disc to
            // the left of each label, and it needs room that is there in both
            // states, so the tab never changes width when the rail folds.
            "group flex min-w-[84px] shrink-0 flex-col items-center gap-1.5 px-5 pb-3 no-underline transition-colors";
          const style = { color: on ? NAVY : META };
          const body = (
            <>
              <span
                ref={i === routeIdx ? landedStage : undefined}
                className="relative flex h-9 w-9 items-center justify-center"
                style={{
                  opacity: compact ? 0 : 1,
                  transform: compact ? "scale(0.6)" : "scale(1)",
                  transition: `opacity 200ms ease, transform 320ms ${EASE}`,
                }}
              >
                {tab.icon ? (
                  <img src={tab.icon} alt="" className="mjfx-icon h-9 w-9 object-contain" />
                ) : (
                  <Icon name={tab.slug ? "ri-restaurant-2-line" : "ri-layout-grid-line"} className="text-[26px]" aria-hidden="true" />
                )}
              </span>
              {/* Folded, the big icon above has gone, so the label carries a
                  small copy of it on its left (owner, option A,
                  2026-09-28; the pewter disc of option C was tried first). Both move by transform only: the disc sits
                  outside the label's box and the pair slides half a disc to
                  stay centred, so nothing reflows while the header folds. */}
              <span
                className="relative whitespace-nowrap"
                style={{
                  transform: compact ? `translateX(${DISC_SHIFT}px)` : "translateX(0)",
                  transition: `transform 320ms ${EASE}`,
                }}
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute top-1/2 flex items-center justify-center rounded-full"
                  style={{
                    insetInlineEnd: "calc(100% + 6px)",
                    width: DISC,
                    height: DISC,
                    marginTop: -DISC / 2,
                    opacity: compact ? 1 : 0,
                    transform: compact ? "scale(1)" : "scale(0.6)",
                    transition: `opacity 200ms ease, transform 320ms ${EASE}`,
                  }}
                >
                  {tab.icon ? (
                    <img src={tab.icon} alt="" className="h-[22px] w-[22px] object-contain" />
                  ) : (
                    <Icon name={tab.slug ? "ri-restaurant-2-line" : "ri-layout-grid-line"} className="text-[18px]" aria-hidden="true" />
                  )}
                </span>
                <span className={`text-[14px] ${on ? "font-bold" : "font-medium"}`}>{tab.name}</span>
              </span>
            </>
          );
          const ref = (el: HTMLAnchorElement | null) => {
            refs.current[i] = el;
          };
          const current = on ? ("page" as const) : undefined;
          const onClick = () => {
            if (i === idx) return;
            if (tab.slug) queueCategoryFx(tab.slug);
            hubSlideDir = i > idx ? 1 : -1;
            elasticNext = true;
            setPending(i);
          };
          if (!tab.slug) {
            return citySlug ? (
              <Link key="all" ref={ref} to="/$lang/place/$city" params={{ lang, city: citySlug }} resetScroll={false} aria-current={current} onClick={onClick} className={className} style={style}>
                {body}
              </Link>
            ) : (
              <Link key="all" ref={ref} to="/$lang/explore" params={{ lang }} resetScroll={false} aria-current={current} onClick={onClick} className={className} style={style}>
                {body}
              </Link>
            );
          }
          return citySlug ? (
            <Link key={tab.slug} ref={ref} to="/$lang/category/$category/$city" params={{ lang, category: tab.slug, city: citySlug }} resetScroll={false} aria-current={current} onClick={onClick} className={className} style={style}>
              {body}
            </Link>
          ) : (
            <Link key={tab.slug} ref={ref} to="/$lang/category/$category" params={{ lang, category: tab.slug }} resetScroll={false} aria-current={current} onClick={onClick} className={className} style={style}>
              {body}
            </Link>
          );
        })}
        {bar ? <ElasticLine x={bar.x} w={bar.w} /> : null}
      </div>
      </div>
    </nav>
  );
}

/**
 * The tab underline, Apple style: it first stretches to cover both the old
 * tab and the new one, then lets go of the old side and settles on the new
 * one with a small overshoot. Transform only: a fixed-width bar scaled on X.
 */
/** Set by a tab tap, read once by the line's next move. */
let elasticNext = false;

function ElasticLine({ x, w }: { x: number; w: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const prev = useRef<{ x: number; w: number } | null>(null);
  // The resting position is always the style itself; the stretch is only an
  // animation played over it. If that animation is cut short (a tab switched
  // mid-flight, a backgrounded page, a remount) the line is still exactly
  // under the active tab. A two-step state machine here once left it parked
  // under the previous tab.
  useEffect(() => {
    const from = prev.current;
    prev.current = { x, w };
    const el = ref.current;
    // Only a tap on a tab stretches the line. Fonts landing or the window
    // resizing just move it, and a hidden page never animates.
    const tapped = elasticNext;
    elasticNext = false;
    if (!el || !from || !tapped || document.hidden || (from.x === x && from.w === w)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const pose = (px: number, pw: number) => `translateX(${px}px) scaleX(${pw / 100})`;
    const left = Math.min(from.x, x);
    const right = Math.max(from.x + from.w, x + w);
    el.getAnimations().forEach((a) => a.cancel());
    el.animate(
      [
        { transform: pose(from.x, from.w), easing: "cubic-bezier(.4,0,1,1)" },
        { transform: pose(left, right - left), offset: 0.34, easing: "cubic-bezier(.34,1.45,.64,1)" },
        { transform: pose(x, w) },
      ],
      { duration: 560 },
    );
  }, [x, w]);
  return (
    <span
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute bottom-0 left-0 h-[3px] w-[100px] origin-left rounded-full"
      style={{ background: NAVY, transform: `translateX(${x}px) scaleX(${w / 100})` }}
    />
  );
}

/**
 * Which way the last tab tap travelled: 1 to the right, -1 to the left. The
 * content below reads it to slide in from that side. Module state, because
 * the tab rail and the page body are siblings and the route may remount them.
 */
let hubSlideDir: 1 | -1 | 0 = 0;

/**
 * The page body under the tabs. When `k` (the tab's category) changes it
 * slides in from the side the guest moved toward, and fades up.
 */
export function HubSlide({ k, children }: { k: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current && hubSlideDir === 0) {
      first.current = false;
      return;
    }
    first.current = false;
    const el = ref.current;
    const dir = hubSlideDir;
    hubSlideDir = 0;
    if (!el || !dir || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    el.animate(
      [
        { transform: `translateX(${dir * 48}px)`, opacity: 0 },
        { transform: "translateX(0)", opacity: 1 },
      ],
      { duration: 420, easing: "cubic-bezier(.32,.72,0,1)" },
    );
  }, [k]);
  return <div ref={ref}>{children}</div>;
}

/**
 * "A city was just tapped, the next page is on its way." Set by the city
 * picker, cleared when the grid gets its new key (or after 5s, in case the
 * tap went nowhere). Kept outside the router so tests need no router.
 */
let hubLoading = false;
let hubLoadingTimer: ReturnType<typeof setTimeout> | undefined;
const hubLoadingListeners = new Set<() => void>();
export function setHubLoading(on: boolean) {
  if (hubLoading === on) return;
  hubLoading = on;
  clearTimeout(hubLoadingTimer);
  if (on) hubLoadingTimer = setTimeout(() => setHubLoading(false), 5000);
  hubLoadingListeners.forEach((l) => l());
}
function onHubLoading(l: () => void) {
  hubLoadingListeners.add(l);
  return () => hubLoadingListeners.delete(l);
}
function isHubLoading() {
  return hubLoading;
}

/**
 * The results grid. While the next city (or filter) loads it dims, so the tap
 * reads as heard; when `k` changes the new cards rise in one after another.
 */
export function HubFade({ k, children }: { k: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const first = useRef(true);
  const pending = useSyncExternalStore(onHubLoading, isHubLoading, () => false);
  useEffect(() => {
    setHubLoading(false);
    if (first.current) {
      first.current = false;
      return;
    }
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const cards = [...el.querySelectorAll<HTMLElement>("article, [data-card]")].slice(0, 8);
    const targets = cards.length ? cards : [el];
    targets.forEach((c, i) =>
      c.animate(
        [
          { transform: "translateY(14px)", opacity: 0 },
          { transform: "translateY(0)", opacity: 1 },
        ],
        { duration: 380, delay: i * 45, easing: "cubic-bezier(.32,.72,0,1)", fill: "backwards" },
      ),
    );
  }, [k]);
  return (
    <div ref={ref} style={{ opacity: pending ? 0.45 : 1, transition: "opacity 200ms ease" }}>
      {children}
    </div>
  );
}

/** How far the rail rides past the header when folded: its icons and gaps. */
const FOLD = 46;
/** The folded row's icon box, and how far each label slides to stay centred with it. */
const DISC = 24;
const DISC_SHIFT = (DISC + 6) / 2;
const EASE = "cubic-bezier(.2,.8,.2,1)";
/** For bars that pin under the rail and must fold with it, in step. */
export const HUB_FOLD = FOLD;
export const HUB_EASE = EASE;

/**
 * Folded while the guest scrolls down; unfolded only back near the top.
 * Also slides the site header away, through one attribute on <html> that
 * `.mj-site-header` answers to.
 */
function useCompactOnScroll() {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const root = document.documentElement;
    const style = document.createElement("style");
    // Folded, the phone's bottom tab bar drops away too and the floating
    // Filters pill slides down into the space it leaves.
    style.textContent = [
      `@media (prefers-reduced-motion:no-preference){.mj-site-header{transition:transform 320ms ${EASE}}}`,
      `html[data-hub-compact] .mj-site-header{transform:translateY(-100%)}`,
      `html[data-hub-compact] .mj-bottom-bar{transform:translateY(calc(100% + 16px))}`,
      `@media (prefers-reduced-motion:no-preference){.mj-hub-filters{transition:transform 320ms ${EASE}}}`,
      `@media (max-width:1023px){html[data-hub-compact] .mj-hub-filters{transform:translateY(68px) scale(0.86)}}`,
    ].join("");
    document.head.appendChild(style);

    let last = window.scrollY;
    let state = false;
    const set = (next: boolean) => {
      if (next === state) return;
      state = next;
      setCompact(next);
      if (next) root.dataset.hubCompact = "";
      else delete root.dataset.hubCompact;
    };
    const onScroll = () => {
      const y = window.scrollY;
      // A scroll the page made itself (keeping a list in place after a filter
      // change) is not the guest scrolling: leave the header where it is.
      if (root.dataset.hubScrollHold !== undefined) {
        last = y;
        return;
      }
      const dy = y - last;
      // Folded on a scroll down. Scrolling back up keeps it folded; the big
      // header and icon row only return near the top. Map view got this first
      // (owner, 2026-09-28), the list followed (owner, 2026-10-04: "when
      // scrolling up no need to switch the header to the big one").
      // Direction is still published, for things that answer to a scroll up,
      // like the Stays summary bar.
      if (y < 160) set(false);
      else if (dy > 6) set(true);
      if (dy > 6) root.dataset.hubDir = "down";
      else if (dy < -10) root.dataset.hubDir = "up";
      if (Math.abs(dy) > 6) last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      delete root.dataset.hubCompact;
      delete root.dataset.hubDir;
      style.remove();
    };
  }, []);
  return compact;
}

/* ------------------------------------------------------------ controls */

/** A control at rest: white + one hairline. No pewter band on controls. */
export const HUB_CONTROL: CSSProperties = {
  background: "#fff",
  boxShadow: "0 0 0 1px rgba(17,14,57,0.12), 0 1px 2px rgba(17,14,57,0.04)",
  color: NAVY,
};
export const HUB_CONTROL_CLASS =
  "inline-flex h-11 items-center gap-2 rounded-full px-4 text-[14px] font-semibold transition-transform active:scale-[0.97]";

/* ------------------------------------------------------------ title row */

export function HubTitleRow({
  lang,
  h1,
  categoryName,
  cityName,
  total,
  controls,
}: {
  lang: string;
  h1: string;
  categoryName?: string | null;
  cityName?: string | null;
  total: number;
  controls: ReactNode;
}) {
  // Breadcrumbs and the result count are gone from view (owner,
  // 2026-09-25). Google still gets the trail from the BreadcrumbList JSON-LD,
  // and the <h1> stays: it is what the page ranks for. It is just quieter now,
  // one line beside the city picker.
  // The heading is off screen too now (owner, 2026-09-26), but it is still
  // the page's one <h1>: Google ranks the page on it and screen readers
  // announce it. What shows is the controls row, city chip at the end.
  return (
    <>
      <h1 className="sr-only">{h1}</h1>
      <div className="flex flex-wrap items-center gap-2 pt-4 lg:ms-auto lg:shrink-0 lg:flex-nowrap lg:gap-3 lg:pt-0">{controls}</div>
    </>
  );
}

/* --------------------------------------------------------------- intro */

/**
 * The hub's long intro, under the grid. Clamped to four lines with a toggle;
 * the whole paragraph is in the HTML either way, so crawlers read all of it.
 */
export function HubIntro({ title, text }: { title: string; text?: string | null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  // The toggle is only offered when the clamp actually hides something.
  useEffect(() => {
    const el = ref.current;
    if (!el || open) return;
    const check = () => setClamped(el.scrollHeight > el.clientHeight + 2);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, open]);

  // A one-line fallback ("Best Things to Do in Morocco") repeats the page
  // title and says nothing; the block is for the real description.
  if (!text || text.trim().length < 140) return null;

  return (
    <section className="mx-auto mt-4 max-w-[860px] py-8">
      <h2 className="m-0 text-[20px] font-bold tracking-[-0.01em]" style={{ color: NAVY }}>
        {title}
      </h2>
      <p
        ref={ref}
        className="m-0 mt-3 text-[15.5px] leading-[1.75]"
        style={{
          color: META,
          display: open ? "block" : "-webkit-box",
          WebkitLineClamp: open ? undefined : 4,
          WebkitBoxOrient: "vertical",
          overflow: open ? "visible" : "hidden",
        }}
      >
        {text}
      </p>
      {clamped || open ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="mt-2 inline-flex min-h-[32px] items-center gap-1 text-[14px] font-semibold underline-offset-4 hover:underline"
          style={{ color: NAVY }}
        >
          {open ? t("common.show_less", { defaultValue: "Show less" }) : t("common.read_more", { defaultValue: "Read more" })}
          <Icon name={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} className="text-[16px]" aria-hidden="true" />
        </button>
      ) : null}
    </section>
  );
}
